import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useUpdateUserMutation } from "@/api/authentication";
import type { DateFormat, TimeFormat } from "@/types/user";
import { formatDateWithLocale, formatTimeWithLocale } from "@/helpers/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestEmailChange: () => void;
}

const DATE_FORMAT_OPTIONS: Array<{ value: DateFormat; label: string }> = [
  { value: "US", label: "US: MM/DD/YYYY" },
  { value: "ISO", label: "ISO: YYYY-MM-DD" },
  { value: "UK_EU", label: "UK/EU: DD/MM/YYYY" },
  { value: "Verbose", label: "Verbose: dd MMM yyyy" },
];

const TIME_FORMAT_OPTIONS: Array<{ value: TimeFormat; label: string }> = [
  { value: "12h", label: "12-hour (AM/PM)" },
  { value: "24h", label: "24-hour" },
];

interface FormData {
  name: string;
  surname: string;
  phoneNumber: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  aiAuthorName: string;
}

const AI_AUTHOR_NAME_MAX_LENGTH = 255;
// Word's `w:author` is an XML attribute: no line breaks or tabs (mirrors the backend rule).
const AI_AUTHOR_NAME_PATTERN = /^[^\r\n\t]*$/;
const defaultAiAuthorName = (name: string, surname: string, username: string): string =>
  `${`${name} ${surname}`.trim() || username} (Pathfinder)`;

export const ProfileEditModal = ({ open, onOpenChange, onRequestEmailChange }: Props) => {
  const user = useAuthenticatedUser();
  const [updateUser, { isLoading }] = useUpdateUserMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: user.name,
      surname: user.surname,
      phoneNumber: user.phoneNumber || "",
      dateFormat: user.dateFormat,
      timeFormat: user.timeFormat,
      aiAuthorName: user.aiAuthorName ?? "",
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        name: user.name,
        surname: user.surname,
        phoneNumber: user.phoneNumber || "",
        dateFormat: user.dateFormat,
        timeFormat: user.timeFormat,
        aiAuthorName: user.aiAuthorName ?? "",
      });
    }
  }, [open, reset, user.name, user.surname, user.phoneNumber, user.dateFormat, user.timeFormat, user.aiAuthorName]);

  const onSubmit = async (data: FormData) => {
    try {
      await updateUser(data).unwrap();

      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  const previewDate = new Date();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 max-h-[85vh] w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          <div className="flex items-start justify-between border-b pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">Edit Profile</Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500">
                Update your profile information and preferences
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium">Profile Information</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">First Name</Label>
                  <Input id="name" {...register("name", { required: true })} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="surname">Last Name</Label>
                  <Input id="surname" {...register("surname", { required: true })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={user.email} disabled className="bg-gray-50 text-gray-500" />
                {user.canSelfServiceEmailChange ? (
                  <div className="flex flex-col items-start gap-2 pt-1">
                    <p className="text-xs text-gray-500">
                      Your current email remains active until you confirm the new address.
                    </p>
                    <Button type="button" variant="outline" size="sm" onClick={onRequestEmailChange}>
                      Change email address
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">
                    Contact your organization administrator to change your email address.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input id="phoneNumber" type="tel" {...register("phoneNumber")} placeholder="+1234567890" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="aiAuthorName">AI author name</Label>
                <Input
                  id="aiAuthorName"
                  maxLength={AI_AUTHOR_NAME_MAX_LENGTH}
                  placeholder={defaultAiAuthorName(user.name, user.surname, user.username)}
                  aria-invalid={errors.aiAuthorName ? true : undefined}
                  {...register("aiAuthorName", {
                    maxLength: AI_AUTHOR_NAME_MAX_LENGTH,
                    pattern: AI_AUTHOR_NAME_PATTERN,
                  })}
                />
                <p className="text-xs text-gray-500">
                  Shown as the author of every tracked change and comment you make in Document Authoring, including
                  Pathfinder&apos;s suggestions. Leave empty to use the default.
                </p>
                {errors.aiAuthorName && (
                  <p className="text-xs text-danger" role="alert">
                    Use up to {AI_AUTHOR_NAME_MAX_LENGTH} characters on a single line.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-4 border-t pt-6">
              <h3 className="text-sm font-medium">Date & Time Preferences</h3>

              <div className="space-y-3">
                <Label>Date Format</Label>
                <div className="space-y-2">
                  {DATE_FORMAT_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        value={option.value}
                        {...register("dateFormat")}
                        className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300"
                      />
                      <div className="flex flex-1 items-center justify-between">
                        <span className="text-sm font-medium">{option.label}</span>
                        <span className="text-sm text-gray-500">
                          Preview: {formatDateWithLocale(previewDate, option.value)}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Label>Time Format</Label>
                <div className="space-y-2">
                  {TIME_FORMAT_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className="flex cursor-pointer items-center space-x-3 rounded-md border p-3 transition-colors hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        value={option.value}
                        {...register("timeFormat")}
                        className="text-atriablue-500 focus:ring-atriablue-500 h-4 w-4 border-gray-300"
                      />
                      <div className="flex flex-1 items-center justify-between">
                        <span className="text-sm font-medium">{option.label}</span>
                        <span className="text-sm text-gray-500">
                          Preview: {formatTimeWithLocale(previewDate, option.value)}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                <p>
                  Organization default: {user.organization.dateFormat} date format, {user.organization.timeFormat} time
                  format
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t pt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
